import { useCallback, useMemo } from 'react';
import type { StoryboardContextValue } from '../../context';
import { useTimelineSelection } from './useTimelineSelection';
import { useTimelineFeedback } from './useTimelineFeedback';
import { usePreviewActions } from './usePreviewActions';
import { useDragManager } from './useDragManager';
import { useSegmentRenderQueue } from './useSegmentRenderQueue';
import type { FeedbackState, FramePickerState, PreviewContextMenuState } from './types';

interface UseSegmentTimelineStateParams
  extends Pick<
    StoryboardContextValue,
    | 'segments'
    | 'mutating'
    | 'activeProjectId'
    | 'activeSegmentId'
    | 'createSegment'
    | 'reorderSegments'
    | 'deleteSegment'
    | 'updateSegment'
    | 'selectSegment'
    | 'focusPreviewEntry'
  > {
  focusedPreviewEntryIds: StoryboardContextValue['focusedPreviewEntryIds'];
}

export interface SegmentTimelineState {
  feedback: FeedbackState;
  selection: ReturnType<typeof useTimelineSelection>['selection'];
  infoAnchorEl: ReturnType<typeof useTimelineSelection>['infoAnchorEl'];
  infoSegmentId: ReturnType<typeof useTimelineSelection>['infoSegmentId'];
  infoArrowRef: ReturnType<typeof useTimelineSelection>['infoArrowRef'];
  setInfoAnchorEl: ReturnType<typeof useTimelineSelection>['setInfoAnchorEl'];
  setInfoSegmentId: ReturnType<typeof useTimelineSelection>['setInfoSegmentId'];
  setInfoArrowRef: ReturnType<typeof useTimelineSelection>['setInfoArrowRef'];
  handleSelectSegmentCard: ReturnType<typeof useTimelineSelection>['handleSelectSegmentCard'];
  handleCloseInfo: ReturnType<typeof useTimelineSelection>['handleCloseInfo'];
  handlePreviewEntry: ReturnType<typeof useTimelineSelection>['handlePreviewEntry'];
  handleAdoptPreview: ReturnType<typeof useTimelineSelection>['handleAdoptPreview'];
  previewContextMenu: PreviewContextMenuState | null;
  openPreviewContextMenu: ReturnType<typeof usePreviewActions>['openPreviewContextMenu'];
  closePreviewContextMenu: ReturnType<typeof usePreviewActions>['closePreviewContextMenu'];
  handleSendRemixToNext: ReturnType<typeof usePreviewActions>['handleSendRemixToNext'];
  handleRemixInCurrentSegment: ReturnType<typeof usePreviewActions>['handleRemixInCurrentSegment'];
  handleOpenFramePicker: ReturnType<typeof usePreviewActions>['handleOpenFramePicker'];
  framePickerState: FramePickerState | null;
  handleFramePickerClose: ReturnType<typeof usePreviewActions>['handleFramePickerClose'];
  handleFrameSelected: ReturnType<typeof usePreviewActions>['handleFrameSelected'];
  hasNextSegmentForMenu: boolean;
  contextMenuVideoId: string;
  isFramePickerBusy: boolean;
  canRemixInCurrentSegment: boolean;
  dragState: ReturnType<typeof useDragManager>['state'];
  handleDragStart: ReturnType<typeof useDragManager>['handleDragStart'];
  handleDragEnd: ReturnType<typeof useDragManager>['handleDragEnd'];
  handleDragOverSegment: ReturnType<typeof useDragManager>['handleDragOverSegment'];
  handleDragLeaveSegment: ReturnType<typeof useDragManager>['handleDragLeaveSegment'];
  handleDropOnSegment: ReturnType<typeof useDragManager>['handleDropOnSegment'];
  handleDragOverAddButton: ReturnType<typeof useDragManager>['handleDragOverAddButton'];
  handleDragLeaveAddButton: ReturnType<typeof useDragManager>['handleDragLeaveAddButton'];
  handleDropOnAddButton: ReturnType<typeof useDragManager>['handleDropOnAddButton'];
  handleCreateSegment: () => Promise<void>;
  handleDeleteSegment: (segmentId: string) => Promise<void>;
  visibleSegments: StoryboardContextValue['segments'];
}

export const useSegmentTimelineState = (
  params: UseSegmentTimelineStateParams
): SegmentTimelineState => {
  const {
    segments,
    mutating,
    activeProjectId,
    activeSegmentId,
    createSegment,
    reorderSegments,
    deleteSegment,
    updateSegment,
    selectSegment,
    focusPreviewEntry,
    focusedPreviewEntryIds,
  } = params;

  const selection = useTimelineSelection({
    segments,
    selectSegment,
    focusPreviewEntry,
    updateSegment,
    focusedPreviewEntryIds,
  });

  const feedback = useTimelineFeedback();

  const findNextSegment = useCallback(
    (segmentId: string) => {
      const index = segments.findIndex((segment) => segment.segmentId === segmentId);
      if (index < 0 || index >= segments.length - 1) {
        return null;
      }
      return segments[index + 1] ?? null;
    },
    [segments]
  );

  const previewActions = usePreviewActions({
    segments,
    updateSegment,
    feedback,
    findNextSegment,
  });

  const dragManager = useDragManager({ segments, mutating, reorderSegments });

  const requiredSegmentIds = useMemo(
    () =>
      [
        activeSegmentId ?? null,
        selection.infoSegmentId,
        dragManager.state.draggingSegmentId,
        dragManager.state.dropTargetSegmentId,
      ].filter((value): value is string => Boolean(value)),
    [
      activeSegmentId,
      dragManager.state.draggingSegmentId,
      dragManager.state.dropTargetSegmentId,
      selection.infoSegmentId,
    ]
  );

  const visibleSegments = useSegmentRenderQueue(segments, requiredSegmentIds);

  const handleCreateSegment = useCallback(async () => {
    if (!activeProjectId) return;
    await createSegment({
      projectId: activeProjectId,
      title: `单元 ${segments.length + 1}`,
    });
  }, [activeProjectId, createSegment, segments.length]);

  const handleDeleteSegment = useCallback(
    async (segmentId: string) => {
      await deleteSegment(segmentId);
      if (selection.infoSegmentId === segmentId) {
        selection.handleCloseInfo();
      }
    },
    [deleteSegment, selection]
  );

  return useMemo<SegmentTimelineState>(
    () => ({
      feedback,
      selection: selection.selection,
      infoAnchorEl: selection.infoAnchorEl,
      infoSegmentId: selection.infoSegmentId,
      infoArrowRef: selection.infoArrowRef,
      setInfoAnchorEl: selection.setInfoAnchorEl,
      setInfoSegmentId: selection.setInfoSegmentId,
      setInfoArrowRef: selection.setInfoArrowRef,
      handleSelectSegmentCard: selection.handleSelectSegmentCard,
      handleCloseInfo: selection.handleCloseInfo,
      handlePreviewEntry: selection.handlePreviewEntry,
      handleAdoptPreview: selection.handleAdoptPreview,
      previewContextMenu: previewActions.previewContextMenu,
      openPreviewContextMenu: previewActions.openPreviewContextMenu,
      closePreviewContextMenu: previewActions.closePreviewContextMenu,
      handleSendRemixToNext: previewActions.handleSendRemixToNext,
      handleRemixInCurrentSegment: previewActions.handleRemixInCurrentSegment,
      handleOpenFramePicker: previewActions.handleOpenFramePicker,
      framePickerState: previewActions.framePickerState,
      handleFramePickerClose: previewActions.handleFramePickerClose,
      handleFrameSelected: previewActions.handleFrameSelected,
      hasNextSegmentForMenu: previewActions.hasNextSegmentForMenu,
      contextMenuVideoId: previewActions.contextMenuVideoId,
      isFramePickerBusy: previewActions.isFramePickerBusy,
      canRemixInCurrentSegment: previewActions.canRemixInCurrentSegment,
      dragState: dragManager.state,
      handleDragStart: dragManager.handleDragStart,
      handleDragEnd: dragManager.handleDragEnd,
      handleDragOverSegment: dragManager.handleDragOverSegment,
      handleDragLeaveSegment: dragManager.handleDragLeaveSegment,
      handleDropOnSegment: dragManager.handleDropOnSegment,
      handleDragOverAddButton: dragManager.handleDragOverAddButton,
      handleDragLeaveAddButton: dragManager.handleDragLeaveAddButton,
      handleDropOnAddButton: dragManager.handleDropOnAddButton,
      handleCreateSegment,
      handleDeleteSegment,
      visibleSegments,
    }),
    [
      dragManager.handleDragEnd,
      dragManager.handleDragLeaveAddButton,
      dragManager.handleDragLeaveSegment,
      dragManager.handleDragOverAddButton,
      dragManager.handleDragOverSegment,
      dragManager.handleDragStart,
      dragManager.handleDropOnAddButton,
      dragManager.handleDropOnSegment,
      dragManager.state,
      feedback,
      handleCreateSegment,
      handleDeleteSegment,
      previewActions.contextMenuVideoId,
      previewActions.framePickerState,
      previewActions.handleFramePickerClose,
      previewActions.handleFrameSelected,
      previewActions.handleOpenFramePicker,
      previewActions.handleSendRemixToNext,
      previewActions.handleRemixInCurrentSegment,
      previewActions.hasNextSegmentForMenu,
      previewActions.isFramePickerBusy,
      previewActions.canRemixInCurrentSegment,
      previewActions.openPreviewContextMenu,
      previewActions.closePreviewContextMenu,
      previewActions.previewContextMenu,
      selection.handleAdoptPreview,
      selection.handleCloseInfo,
      selection.handlePreviewEntry,
      selection.handleSelectSegmentCard,
      selection.infoAnchorEl,
      selection.infoArrowRef,
      selection.infoSegmentId,
      selection.selection,
      selection.setInfoAnchorEl,
      selection.setInfoArrowRef,
      selection.setInfoSegmentId,
      visibleSegments,
    ]
  );
};
